import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import Select from "react-select";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import MobileCapture from "../../../ui/MobileCapture";
import FileUploadSection from "../../../ui/FileUploadSection";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageRubrics = () => {
  const [rubrics, setRubrics] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newRubric, setNewRubric] = useState({
    name: "",
    file: null,
    text: "",
    instructorId: "",
    capturedFile: null,
  });
  const { user } = useContext(AuthContext);

  const [editRubric, setEditRubric] = useState({
    id: "",
    name: null,
    text: "",
    file: null,
    fileName: "",
    capturedFile: null,
    removeFile: false,
    original: {},
  });
  const [initialEditRubric, setInitialEditRubric] = useState(null);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewText, setPreviewText] = useState("");
  const [isPreviewTextOpen, setIsPreviewTextOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false); // New state for capture mode
  const [captureFor, setCaptureFor] = useState(null); // 'create' or 'edit'

  const hasFetched = useRef(false);

  const columns = [
    { key: "name", label: "Name", sortable: true, filter: true },
    {
      key: "instructorName",
      label: "Instructor",
      sortable: true,
      filter: true,
    },
    { key: "formattedDate", label: "Created Date", sortable: true },
    { key: "preview", label: "", sortable: false },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  const userDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return new Date(date.getTime()).toLocaleString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  }, []);

  const fetchInstructors = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/get-all/${user.instituteId}/instructor`,
        {
          credentials: "include",
        },
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch instructors");
      const data = await response.json();
      setInstructors(data);
    } catch (err) {
      swalError(err.message);
    }
  }, [user.instituteId]);

  const fetchRubrics = useCallback(async () => {
    try {
      setLoading(true);
      const allRubrics = [];
      for (const instructor of instructors) {
        const response = await fetchWithAuth(
          `${serverUrl}/api/rubric/get-all/${instructor._id}`,
          {
            credentials: "include",
          },
        );
        if (!response) continue;
        if (!response.ok) continue;
        const data = await response.json();
        const rubricsWithInstructor = data.map((rubric) => ({
          ...rubric,
          instructorId: instructor._id,
          instructorName: `${instructor.firstName} ${instructor.lastName}`,
          formattedDate: userDate(rubric.createdAt),
        }));
        allRubrics.push(...rubricsWithInstructor);
      }
      setRubrics(allRubrics);
      setError(null);
    } catch (err) {
      setError(err.message);
      swalError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instructors, userDate]);

  useEffect(() => {
    if (hasFetched.current) return;
    fetchInstructors();
    hasFetched.current = true;
  }, [fetchInstructors]);

  useEffect(() => {
    if (instructors.length > 0) {
      fetchRubrics();
    }
  }, [instructors, fetchRubrics]);

  const handleCreateRubric = async (e) => {
    e.preventDefault();
    if (!newRubric.instructorId) {
      swalError("Please select an Instructor.");
      return;
    }
    if (!newRubric.text && !newRubric.file) {
      swalError("Either text or file must be provided.");
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("name", newRubric.name);
    formData.append("instructorId", newRubric.instructorId);

    if (newRubric.file) {
      formData.append("file", newRubric.file);
    } else if (newRubric.text) {
      formData.append("text", newRubric.text);
    }

    try {
      const response = await fetchWithAuth(`${serverUrl}/api/rubric/add`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to create rubric");
      }
      const message = data.message || "Rubric created successfully.";
      swalSuccess(message);
      fetchRubrics();
    } catch (error) {
      swalError("Failed to create rubric");
    } finally {
      setIsSubmitting(false);
      setIsCreateModalOpen(false);
      setNewRubric({
        name: "",
        file: null,
        text: "",
        instructorId: "",
        capturedFile: null,
      });
    }
  };

  const handleDeleteRubric = async (rubricId) => {
    const result = await swalConfirm(
      "Are you sure you want to delete this rubric?",
      "",
      "Yes, delete it!",
      "No, Cancel",
    );

    if (result.isConfirmed) {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/rubric/delete/${rubricId}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to delete rubric");
        setRubrics((prevRubrics) =>
          prevRubrics.filter((rubric) => rubric._id !== rubricId),
        );
        swalSuccess("Rubric deleted successfully.");
      } catch (err) {
        setError(err.message);
        swalError("Failed to delete rubric");
      }
    }
  };

  const handleEditRubric = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("name", editRubric.name);

    if (editRubric.removeFile) {
      formData.append("rubricFile", "");
    } else if (editRubric.file) {
      formData.append("file", editRubric.file);
    } else if (editRubric.text) {
      formData.append("text", editRubric.text);
    }

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/rubric/update/${editRubric.id}`,
        {
          method: "PUT",
          credentials: "include",
          body: formData,
        },
      );
      if (!response) return;
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update rubric");
      const message = data.message || "Rubric updated successfully.";
      swalSuccess(message);
      fetchRubrics();
    } catch (error) {
      swalError("Failed to update rubric");
    } finally {
      setIsSubmitting(false);
      setIsEditModalOpen(false);
      setEditRubric({
        id: "",
        name: null,
        text: "",
        file: null,
        fileName: "",
        capturedFile: null,
        removeFile: false,
        original: {},
      });
      setInitialEditRubric(null);
      setIsEditChanged(false);
    }
  };

  const handlePreview = (rubric) => {
    if (rubric.url) {
      window.open(rubric.url, "_blank");
    } else if (rubric.text) {
      setPreviewText(rubric.text);
      setIsPreviewTextOpen(true);
    } else {
      swalWarning("No content to preview.");
    }
  };

  const handleEditClick = (rubric) => {
    setEditRubric({
      id: rubric._id,
      name: rubric.name,
      fileName: rubric.fileName,
      text: rubric.text || "",
      file: null,
      capturedFile: null,
      removeFile: false,
      original: {
        name: rubric.name,
        url: rubric.url,
        fileName: rubric.fileName,
        text: rubric.text,
      },
    });
    setInitialEditRubric({
      name: rubric.name,
      text: rubric.text || "",
      fileName: rubric.fileName,
      removeFile: false,
    });
    setIsEditModalOpen(true);
    setIsEditChanged(false);
  };

  const enhancedRubrics = rubrics.map((rubric) => ({
    ...rubric,
    preview: (
      <button
        className="preview-btn"
        onClick={() => handlePreview(rubric)}
        style={{ border: "none", background: "none", cursor: "pointer" }}
      >
        <i className="fa-regular fa-eye"></i>
      </button>
    ),
    edit: (
      <button className="edit-btn" onClick={() => handleEditClick(rubric)}>
        <i className="fa-solid fa-pen-to-square"></i>
      </button>
    ),
    delete: (
      <button
        className="delete-btn"
        onClick={() => handleDeleteRubric(rubric._id)}
      >
        <i className="fa-solid fa-trash-can"></i>
      </button>
    ),
  }));

  // Handle Edit Input Change
  const handleEditInputChange = (e) => {
    const { name, value, files } = e.target;
    const updatedValue = files ? files[0] : value;
    setEditRubric((prev) => ({
      ...prev,
      [name]: updatedValue,
      ...(name === "file" &&
        files && { capturedFile: null, removeFile: false }), // Reset captured file and remove flag when new file is selected
    }));

    if (initialEditRubric) {
      const originalValue = initialEditRubric[name];
      if (files) {
        setIsEditChanged(files[0] !== originalValue);
      } else {
        setIsEditChanged(updatedValue !== originalValue);
      }
    }
  };

  useEffect(() => {
    if (editRubric && initialEditRubric) {
      const fieldsToCheck = ["name", "text", "fileName", "removeFile"];
      const isChanged = fieldsToCheck.some(
        (key) => editRubric[key] !== initialEditRubric[key],
      );
      setIsEditChanged(isChanged);
    } else {
      setIsEditChanged(false);
    }
  }, [editRubric, initialEditRubric]);

  // Callback when capture is done
  const onCaptureComplete = (pdfFile) => {
    // Call the stored callback from FileUploadSection
    if (window.__fileUploadCallback) {
      window.__fileUploadCallback(pdfFile);
      window.__fileUploadCallback = null;
    }

    // Keep existing logic for backward compatibility
    if (captureFor === "create") {
      setNewRubric((prev) => ({
        ...prev,
        file: pdfFile,
        capturedFile: pdfFile,
        text: "",
      }));
      setIsCreateModalOpen(true);
    } else if (captureFor === "edit") {
      setEditRubric((prev) => ({
        ...prev,
        file: pdfFile,
        capturedFile: pdfFile,
      }));
      setIsEditChanged(true);
      setIsEditModalOpen(true);
    }
    setIsCapturing(false);
    setCaptureFor(null);
  };

  // Add close handler for capture
  const handleCloseCapture = () => {
    setIsCapturing(false);
    if (captureFor === "create") {
      setIsCreateModalOpen(true); // Reopen create modal when closing capture without completing
    } else if (captureFor === "edit") {
      setIsEditModalOpen(true); // Reopen edit modal when closing capture without completing
    }
    setCaptureFor(null);
  };

  // Render Rubric File Upload Section
  const renderRubricFileUpload = (isEdit = false) => {
    const rubric = isEdit ? editRubric : newRubric;
    const hasText = rubric.text;
    const hasOriginalFile =
      isEdit && rubric.original.fileName && !rubric.removeFile;

    return (
      <div className="form-group">
        <label>Upload File {!isEdit && "or Enter Text"}</label>

        <FileUploadSection
          currentFileUrl={hasOriginalFile ? rubric.original.url : null}
          currentFileName={
            hasOriginalFile ? rubric.original.fileName.split("/").pop() : null
          }
          isSubmitting={isSubmitting}
          accept=".pdf,.png,.jpg,.jpeg"
          onFileChange={(file, isCaptured) => {
            if (isEdit) {
              setEditRubric((prev) => ({
                ...prev,
                file: file,
                capturedFile: isCaptured ? file : null,
                removeFile: false, // Reset remove flag when new file is selected
                text: "", // Clear text when file is selected
              }));
              setIsEditChanged(true);
            } else {
              setNewRubric((prev) => ({
                ...prev,
                file: file,
                capturedFile: isCaptured ? file : null,
                text: "", // Clear text when file is selected
              }));
            }
          }}
          onCapture={(callback) => {
            setCaptureFor(isEdit ? "edit" : "create");
            setIsCapturing(true);
            if (isEdit) {
              setIsEditModalOpen(false);
            } else {
              setIsCreateModalOpen(false);
            }
            // Store callback for later use
            window.__fileUploadCallback = callback;
          }}
          onPreviewFile={(url) => {
            window.open(url, "_blank");
          }}
          selectedFile={rubric.file}
          isCaptured={rubric.capturedFile !== null}
          browseTitle="Choose File"
          browseDescription="Select PDF or image file from device"
          captureTitle="Capture with Camera"
          captureDescription="Take photos and convert to PDF"
          additionalDisableCondition={hasText}
        />

        <small className="helper-text">
          Upload a rubric file (pdf, jpg, png only) or capture using camera
          {!isEdit && " (only on mobile phone)"}
        </small>
      </div>
    );
  };

  const instructorOptions = instructors.map((instructor) => ({
    value: instructor._id,
    label: `${instructor.firstName} ${instructor.lastName} (${instructor.email})`,
  }));

  return (
    <>
      {/* Capture Full Screen - Show this instead of the main content when capturing */}
      {isCapturing ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            background: "white",
          }}
        >
          <MobileCapture
            onComplete={onCaptureComplete}
            onClose={handleCloseCapture}
            mode="rubric"
          />
        </div>
      ) : (
        <>
          <div className="manage-container">
            <div className="header">
              <button
                className="create-btn"
                onClick={() => setIsCreateModalOpen(true)}
              >
                Add Rubric +
              </button>
            </div>
          </div>
          <div className="manage-container">
            {loading && <Loader />}
            {error && <div className="error-message">{error}</div>}
            {!loading && !error && (
              <Table
                data={enhancedRubrics}
                columns={columns}
                caption="Rubrics"
              />
            )}
          </div>

          {/* Add Rubric Modal */}
          <Modal
            isOpen={isCreateModalOpen}
            onClose={() => !isSubmitting && setIsCreateModalOpen(false)}
          >
            <h2>Add New Rubric</h2>
            <p className="modal-description" style={{ marginBottom: "1rem" }}>
              Fill in the details below to add a new rubric.
            </p>
            <form onSubmit={handleCreateRubric} className="rubric-form">
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i>{" "}
                  Instructor
                </label>
                <Select
                  id="instructorId"
                  name="instructorId"
                  options={instructorOptions}
                  value={
                    newRubric.instructorId
                      ? instructorOptions.find(
                          (option) => option.value === newRubric.instructorId,
                        ) || null
                      : null
                  }
                  onChange={(selectedOption) => {
                    setNewRubric({
                      ...newRubric,
                      instructorId: selectedOption ? selectedOption.value : "",
                    });
                  }}
                  isClearable
                  placeholder="Select or search instructor..."
                  required
                  isDisabled={isSubmitting}
                />
                <small>
                  You can search for an instructor by name or email.
                </small>
              </div>
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Rubric
                  Name
                </label>
                <input
                  type="text"
                  value={newRubric.name}
                  required
                  placeholder="Enter a descriptive rubric name"
                  onChange={(e) =>
                    setNewRubric({ ...newRubric, name: e.target.value })
                  }
                  disabled={isSubmitting}
                />
              </div>

              {renderRubricFileUpload(false)}

              <div className="form-group">
                <label>
                  Rubric Text <span className="optional-text">(Optional)</span>
                </label>
                <textarea
                  type="text"
                  value={newRubric.text}
                  placeholder="Enter rubric text here..."
                  disabled={
                    newRubric.file || newRubric.capturedFile || isSubmitting
                  }
                  onChange={(e) =>
                    setNewRubric({ ...newRubric, text: e.target.value })
                  }
                  rows={4}
                />
                <small className="helper-text">
                  You can either upload a file, capture using camera, or enter
                  rubric text, not multiple options.
                </small>
              </div>
              {isSubmitting ? (
                <Loader />
              ) : (
                <button type="submit" className="btn-submit">
                  Create Rubric
                </button>
              )}
            </form>
          </Modal>

          {/* Edit Rubric Modal */}
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => {
              if (!isSubmitting) {
                setIsEditModalOpen(false);
                setEditRubric({
                  id: "",
                  name: null,
                  text: "",
                  file: null,
                  fileName: "",
                  capturedFile: null,
                  removeFile: false,
                  original: {},
                });
                setInitialEditRubric(null);
                setIsEditChanged(false);
              }
            }}
          >
            <h2>Edit Rubric</h2>
            <p className="modal-description" style={{ marginBottom: "1rem" }}>
              Update the details below to edit the rubric information.
            </p>
            <form onSubmit={handleEditRubric} className="rubric-form">
              <div className="form-group">
                <label>
                  <i className="fa-solid fa-asterisk requiredIcon"></i> Rubric
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={editRubric?.name || ""}
                  required
                  placeholder="Enter a descriptive rubric name"
                  onChange={handleEditInputChange}
                  disabled={isSubmitting}
                />
              </div>

              {editRubric.original.url && renderRubricFileUpload(true)}

              {editRubric.original.text && (
                <div className="form-group">
                  <label>
                    Rubric Text{" "}
                    <span className="optional-text">(Optional)</span>
                  </label>
                  <textarea
                    name="text"
                    value={editRubric?.text || ""}
                    placeholder="Edit rubric text here..."
                    onChange={handleEditInputChange}
                    rows={4}
                    disabled={
                      editRubric.file || editRubric.capturedFile || isSubmitting
                    }
                  />
                  <small className="helper-text">
                    You can update the rubric text here.
                  </small>
                </div>
              )}
              {isSubmitting ? (
                <Loader />
              ) : (
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={!isEditChanged}
                  onClick={(e) => {
                    if (!isEditChanged) {
                      e.preventDefault();
                      swalWarning("No changes to be saved!");
                    }
                  }}
                >
                  Update Rubric
                </button>
              )}
            </form>
          </Modal>

          {/* Preview Text Modal */}
          <Modal
            isOpen={isPreviewTextOpen}
            onClose={() => setIsPreviewTextOpen(false)}
          >
            <h2>Rubric Text</h2>
            <div className="rubric-text-preview">
              <p>{previewText}</p>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default ManageRubrics;
